/* assets/account.js */
(() => {
  const root = document.querySelector('[data-unified-account-orders]');
  if (!root || root.dataset.initialised === 'true') return;
  root.dataset.initialised = 'true';

  const endpoint = '/apps/excision-order-history';
  const listView = root.querySelector('[data-account-orders-list]');
  const detailView = root.querySelector('[data-account-orders-detail]');
  const detailContent = root.querySelector('[data-account-order-detail-content]');
  const backButton = root.querySelector('[data-account-order-back]');
  const searchForm = root.querySelector('[data-unified-order-search]');
  const searchInput = searchForm.querySelector('input[name="q"]');
  const results = root.querySelector('[data-unified-order-results]');
  const orderCount = document.querySelector('#tab-orders .sidebar-tab__count');
  const state = { page: 1, pageSize: 20, q: '', orders: [], controller: null };

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  function formatDate(value) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric'
    }).format(date);
  }

  function formatMoney(value, currency) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency', currency: currency || 'AUD'
    }).format(Number(value || 0));
  }

  function formatOrderNumber(value) {
    return String(value == null ? '' : value).replace(/^SO\s+/i, '');
  }

  function invoiceAccountUrl(invoiceNumber) {
    return window.location.pathname + '?invoice=' + encodeURIComponent(invoiceNumber) + '#invoices';
  }

  function orderAccountUrl(orderKey) {
    const url = new URL(window.location.href);
    url.searchParams.delete('invoice');
    url.searchParams.set('order', orderKey);
    url.hash = 'orders';
    return url.pathname + url.search + url.hash;
  }

  function updateOrderUrl(orderKey, mode) {
    const url = new URL(window.location.href);
    url.searchParams.delete('invoice');
    if (orderKey) url.searchParams.set('order', orderKey);
    else url.searchParams.delete('order');
    url.hash = 'orders';
    window.history[mode === 'replace' ? 'replaceState' : 'pushState'](
      { order: orderKey || '' },
      '',
      url.pathname + url.search + url.hash
    );
  }

  function statusBadge(status) {
    const variant = status === 'Fulfilled' ? 'badge--primary' :
      status === 'Cancelled' ? 'badge--grey' : 'badge--outline';
    return '<span class="badge ' + variant + '">' + escapeHtml(status || 'In progress') + '</span>';
  }

  function outstandingQuantity(line) {
    const value = line.unsuppliedQuantity == null
      ? Math.max(0, Number(line.quantity || 0) - Number(line.suppliedQuantity || 0))
      : line.unsuppliedQuantity;
    return Math.max(0, Number(value || 0));
  }

  function formatAvailabilityWeek(value) {
    if (!value) return '';
    const parsed = new Date(String(value) + 'T00:00:00Z');
    if (Number.isNaN(parsed.getTime())) return '';

    const date = new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()));
    const day = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - day);

    const isoYear = date.getUTCFullYear();
    const yearStart = new Date(Date.UTC(isoYear, 0, 1));
    const isoWeek = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
    return 'Week ' + isoWeek + ' ' + isoYear;
  }

  function availabilityPlaceholder(line) {
    if (!line.sku || outstandingQuantity(line) <= 0) {
      return '<span class="text-ps text-iron">Supplied</span>';
    }
    return '<div class="ex-stock-cell" data-line-availability="' + escapeHtml(line.sku.toUpperCase()) + '" data-outstanding-quantity="' + escapeHtml(outstandingQuantity(line)) + '">' +
      '<span class="text-ps text-iron">Checking stock…</span>' +
    '</div>';
  }

  function availabilityMarkup(item, requestedQuantity) {
    if (!item || item.found !== true || item.active === false) {
      return '<div class="ex-card-badge" data-status="red">No stock date available</div>';
    }

    const available = Math.max(0, Number(item.sellableQty || 0));
    const incoming = Math.max(0, Number(item.incomingQty || 0));
    const hasRestockDate = Boolean(item.restockDate);
    const due = window.MYOB_DEBTOR_ID ? formatAvailabilityWeek(item.restockDate) : '';

    if (available >= requestedQuantity) {
      return '<div class="ex-card-badge" data-status="green">In stock</div>';
    }

    if (available > 0) {
      const details = [available + ' currently available'];
      if (incoming > 0) details.push(incoming + ' expected');
      if (due) details.push('Restocking est ' + due);

      return '<div class="ex-card-badge" data-status="orange">Low stock</div>' +
        details.map((detail) => '<span class="text-ps text-iron">' + escapeHtml(detail) + '</span>').join('');
    }

    if (hasRestockDate) {
      return '<div class="ex-card-badge" data-status="blue">' +
        (due ? 'Restocking est ' + escapeHtml(due) : 'Restocking') +
        '</div>' +
        (incoming > 0 ? '<span class="text-ps text-iron">' + escapeHtml(incoming + ' expected') + '</span>' : '');
    }

    return '<div class="ex-card-badge" data-status="red">No stock date available</div>';
  }

  async function loadLineAvailability(lines) {
    const stockCodes = Array.from(new Set(lines
      .filter((line) => line.sku)
      .map((line) => String(line.sku).trim().toUpperCase())
      .filter(Boolean)));

    if (!stockCodes.length) return;

    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('action', 'line-availability');
    stockCodes.forEach((stockCode) => url.searchParams.append('sku', stockCode));

    try {
      const response = await fetch(url.toString());
      const data = await readApiResponse(response);
      if (!response.ok || data.ok === false) throw new Error(data.error || 'Availability request failed');

      const bySku = new Map((Array.isArray(data.items) ? data.items : []).map((item) => [
        String(item.stockCode || '').trim().toUpperCase(), item
      ]));

      detailContent.querySelectorAll('[data-line-availability]').forEach((node) => {
        const sku = String(node.dataset.lineAvailability || '').toUpperCase();
        const requestedQuantity = Math.max(1, Number(node.dataset.outstandingQuantity || 1));
        node.innerHTML = availabilityMarkup(bySku.get(sku), requestedQuantity);
      });

      detailContent.querySelectorAll('[data-line-product]').forEach((node) => {
        const sku = String(node.dataset.lineProduct || '').toUpperCase();
        const item = bySku.get(sku);
        const productUrl = item && item.productUrl ? String(item.productUrl) : '';
        if (!/^https?:\/\//i.test(productUrl)) return;

        const link = document.createElement('a');
        link.href = productUrl;
        link.innerHTML = node.innerHTML;
        node.replaceWith(link);
      });
    } catch (error) {
      console.error('Order line availability failed:', error);
      detailContent.querySelectorAll('[data-line-availability]').forEach((node) => {
        node.innerHTML = '<div class="ex-card-badge" data-status="grey">Availability unavailable</div>';
      });
    }
  }

  function showList(updateHistory) {
    detailView.classList.add('hidden');
    listView.classList.remove('hidden');
    detailContent.innerHTML = '';
    document.title = document.documentElement.dataset.accountPageTitle || document.title;
    if (updateHistory) updateOrderUrl('', 'push');
  }

  function showNotice(message, variant) {
    results.innerHTML = '<div class="notice notice--' + (variant || 'info') + '">' + escapeHtml(message) + '</div>';
  }

  function renderList(data) {
    state.orders = Array.isArray(data.orders) ? data.orders : [];
    if (orderCount) orderCount.textContent = String(data.total || 0);

    if (data.linked === false) {
      showNotice('Your online account is not yet linked to an Excision account. Please contact us if you need historical orders connected.', 'warning');
      return;
    }
    if (!state.orders.length) {
      showNotice(state.q ? 'No orders matched your search.' : 'No orders have been placed on this account yet.', 'info');
      return;
    }

    const rows = state.orders.map((order) => {
      const reference = order.customerReference || order.reference || order.invoiceNumbers || '';
      return '<tr class="account-orders__clickable-row" data-unified-order-key="' + escapeHtml(order.key) + '" role="link" tabindex="0" aria-label="Open order ' + escapeHtml(formatOrderNumber(order.displayNumber)) + '">' +
        '<td><a class="account-orders__order-link" data-unified-order-key="' + escapeHtml(order.key) + '" href="' + escapeHtml(orderAccountUrl(order.key)) + '">' + escapeHtml(formatOrderNumber(order.displayNumber)) + '</a></td>' +
        '<td class="account-orders__date"><time datetime="' + escapeHtml(order.orderDate) + '">' + escapeHtml(formatDate(order.orderDate)) + '</time></td>' +
        '<td>' + escapeHtml(reference) + '</td>' +
        '<td>' + statusBadge(order.status) + '</td>' +
        '<td class="text-right font-bold">' + escapeHtml(formatMoney(order.total, order.currency)) + '</td>' +
      '</tr>';
    }).join('');

    let pagination = '';
    if (data.pages > 1) {
      pagination = '<div class="account-orders__pagination">' +
        '<button type="button" class="btn btn-outline btn--small" data-unified-page="' + (data.page - 1) + '"' + (data.page <= 1 ? ' disabled' : '') + '>Previous</button>' +
        '<span class="text-iron">Page ' + data.page + ' of ' + data.pages + '</span>' +
        '<button type="button" class="btn btn-outline btn--small" data-unified-page="' + (data.page + 1) + '"' + (data.page >= data.pages ? ' disabled' : '') + '>Next</button>' +
      '</div>';
    }

    results.innerHTML = '<div class="account-orders__table-wrap">' +
      '<table class="table-alternate account-orders__table">' +
        '<thead><tr><th>Order</th><th class="account-orders__date">Date</th><th>Reference</th><th>Status</th><th class="text-right">Total</th></tr></thead>' +
        '<tbody>' + rows + '</tbody>' +
      '</table>' +
    '</div>' + pagination;
  }

  async function readApiResponse(response) {
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (error) {
      const contentType = response.headers.get('content-type') || 'unknown content type';
      const titleMatch = text.match(/<title[^>]*>([^<]*)<\/title>/i);
      const title = titleMatch ? ' Page: ' + titleMatch[1].trim() + '.' : '';
      throw new Error('The order service returned HTTP ' + response.status + ' (' + contentType + ') from ' + response.url + '.' + title);
    }
  }

  async function loadOrders() {
    if (state.controller) state.controller.abort();
    state.controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    showNotice('Loading order history…', 'info');

    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('action', 'list');
    url.searchParams.set('page', String(state.page));
    url.searchParams.set('pageSize', String(state.pageSize));
    if (state.q) url.searchParams.set('q', state.q);

    try {
      const response = await fetch(url.toString(), state.controller ? { signal: state.controller.signal } : {});
      const data = await readApiResponse(response);
      if (!response.ok || data.ok === false) throw new Error(data.error || 'Order history request failed');
      renderList(data);
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      console.error('Unified order history failed:', error);
      const detail = error && error.message ? ' ' + error.message : '';
      showNotice('Order history could not be loaded.' + detail, 'error');
    } finally {
      state.controller = null;
    }
  }

  function trackingUrl(url, number, carrier) {
    const supplied = String(url || '').trim();
    try {
      const parsed = new URL(supplied);
      if (parsed.protocol === 'https:' || parsed.protocol === 'http:') return parsed.href;
    } catch (error) {}

    const trackingNumber = String(number || '').trim();
    const carrierKey = String(carrier || '').trim().toUpperCase();
    if (!trackingNumber) return '';
    if (carrierKey === 'TOLL IPEC' || carrierKey === 'TGE' || carrierKey.includes('TEAM GLOBAL') || carrierKey.includes('MYTEAMGE')) {
      return 'https://www.myteamge.com/?externalSearchQuery=' + encodeURIComponent(trackingNumber);
    }
    if (carrierKey.includes('DIRECT FRT') || carrierKey.includes('DIRECT FREIGHT')) {
      return 'https://www.directfreight.com.au/ConsignmentStatus.aspx?consignment_no=' + encodeURIComponent(trackingNumber);
    }
    if (carrierKey.includes('TNT')) {
      return 'https://www.tntexpress.com.au/Interaction/asps/cnmhxas.asp?' + encodeURIComponent(trackingNumber);
    }
    return '';
  }

  function safeTrackingLink(url, number, carrier) {
    const destination = trackingUrl(url, number, carrier);
    if (!destination) return escapeHtml(number);
    return '<a href="' + escapeHtml(destination) + '" target="_blank" rel="noopener">' + escapeHtml(number || 'Track delivery') + '</a>';
  }

  function renderDetail(order) {
    const lines = Array.isArray(order.lines) ? order.lines : [];
    const invoices = Array.isArray(order.invoices) ? order.invoices : [];
    const address = Array.isArray(order.deliveryAddress) ? order.deliveryAddress : [];
    const invoiceNumbers = invoices.map((invoice) => invoice.invoiceNumber).filter(Boolean);
    const lineRows = lines.map((line) => {
      const invoiced = line.invoicedQuantity == null ? '—' : line.invoicedQuantity;
      const backorderedQuantity = Math.max(0, Number(line.backorderedQuantity || 0));
      const backordered = backorderedQuantity > 0 ? backorderedQuantity : '—';
      return '<tr>' +
        '<td><span data-line-product="' + escapeHtml(String(line.sku || '').trim().toUpperCase()) + '"><span class="font-bold">' + escapeHtml(line.sku) + '</span><br><span class="text-iron">' + escapeHtml(line.description) + '</span></span></td>' +
        '<td class="text-right">' + escapeHtml(line.quantity) + '</td>' +
        '<td class="text-right">' + escapeHtml(invoiced) + '</td>' +
        '<td class="text-right">' + escapeHtml(backordered) + '</td>' +
        '<td>' + availabilityPlaceholder(line) + '</td>' +
        '<td class="text-right">' + escapeHtml(formatMoney(line.unitPrice, order.currency)) + '</td>' +
        '<td class="text-right font-bold">' + escapeHtml(formatMoney(line.lineTotal, order.currency)) + '</td>' +
      '</tr>';
    }).join('');

    const invoiceMarkup = invoices.length ? '<div class="account-orders__documents">' + invoices.map((invoice) => {
      const hasInvoice = Boolean(invoice.invoiceNumber);
      const title = hasInvoice ? 'Invoice ' + invoice.invoiceNumber : 'Delivery tracking';
      const titleMarkup = hasInvoice
        ? '<a class="account-orders__document-link" href="' + escapeHtml(invoiceAccountUrl(invoice.invoiceNumber)) + '">' + escapeHtml(title) + '</a>'
        : '<span class="font-bold">' + escapeHtml(title) + '</span>';
      const meta = [];
      if (invoice.invoiceDate) meta.push('Issued ' + formatDate(invoice.invoiceDate));
      if (invoice.amount != null && hasInvoice) meta.push(formatMoney(invoice.amount, order.currency));
      const tracking = invoice.trackingNumber
        ? '<div class="account-orders__document-tracking"><i class="ph ph-truck" aria-hidden="true"></i><span>Tracking ' + safeTrackingLink(invoice.trackingUrl, invoice.trackingNumber, invoice.trackingCompany) + (invoice.trackingCompany ? ' via ' + escapeHtml(invoice.trackingCompany) : '') + '</span></div>'
        : '';
      return '<div class="account-orders__document">' +
        '<i class="ph ph-file-text account-orders__document-icon" aria-hidden="true"></i>' +
        '<div><div>' + titleMarkup + '</div>' +
        (meta.length ? '<div class="text-ps text-iron">' + escapeHtml(meta.join(' · ')) + '</div>' : '') +
        tracking + '</div></div>';
    }).join('') + '</div>' : '<p class="text-iron mb-0">No invoice has been issued yet.</p>';

    detailContent.innerHTML =
      '<div class="account-orders__detail-heading">' +
        '<div><h2 class="text-h3 mb-2" data-order-detail-heading>' + escapeHtml(formatOrderNumber(order.displayNumber)) + '</h2>' +
        '<p class="text-iron mb-0">Placed ' + escapeHtml(formatDate(order.orderDate)) + '</p></div>' +
        statusBadge(order.status) +
      '</div>' +
      '<dl class="account-orders__meta-list">' +
        '<div><dt>Customer reference</dt><dd>' + escapeHtml(order.customerReference || order.reference || '—') + '</dd></div>' +
        '<div><dt>Invoices</dt><dd>' + escapeHtml(invoiceNumbers.length ? invoiceNumbers.join(', ') : (order.invoiceNumbers || 'Not yet invoiced')) + '</dd></div>' +
        '<div><dt>Order total</dt><dd class="font-bold">' + escapeHtml(formatMoney(order.total, order.currency)) + '</dd></div>' +
      '</dl>' +
      '<div class="account-orders__table-wrap mb-6"><table class="table-alternate account-orders__table account-orders__line-table">' +
        '<thead><tr><th>Part</th><th class="text-right">Ordered</th><th class="text-right">Invoiced</th><th class="text-right">Backordered</th><th>Availability</th><th class="text-right">Unit price</th><th class="text-right">Total</th></tr></thead>' +
        '<tbody>' + lineRows + '</tbody></table></div>' +
      '<div class="split-grid">' +
        '<div><h3 class="text-h4 mb-3">Delivery address</h3><p class="mb-0">' + (address.length ? address.map(escapeHtml).join('<br>') : '<span class="text-iron">Not available</span>') + '</p></div>' +
        '<div><h3 class="text-h4 mb-3">Invoices &amp; tracking</h3>' + invoiceMarkup + '</div>' +
      '</div>';

    listView.classList.add('hidden');
    detailView.classList.remove('hidden');
    loadLineAvailability(lines);
    const heading = detailContent.querySelector('[data-order-detail-heading]');
    if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus(); }
  }

  async function openOrder(key, updateHistory) {
    const orderKey = String(key || '').trim();
    if (!orderKey) return;

    if (updateHistory) updateOrderUrl(orderKey, 'push');
    listView.classList.add('hidden');
    detailView.classList.remove('hidden');
    detailContent.innerHTML = '<div class="notice notice--info">Loading order…</div>';

    try {
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('action', 'detail');
      url.searchParams.set('orderKey', orderKey);
      const response = await fetch(url.toString());
      const data = await readApiResponse(response);
      if (!response.ok || data.ok === false) throw new Error(data.error || 'Order request failed');
      renderDetail(data.order);
      document.title = 'Order ' + formatOrderNumber(data.order.displayNumber) + ' - ' + document.title.replace(/^Order .*? - /, '');
    } catch (error) {
      console.error('Unified order detail failed:', error);
      detailContent.innerHTML = '<div class="notice notice--error">This order could not be loaded. Please return to the list and try again.</div>';
    }
  }

  searchForm.addEventListener('submit', (event) => {
    event.preventDefault();
    state.q = searchInput.value.trim();
    state.page = 1;
    showList(false);
    loadOrders();
  });

  results.addEventListener('click', (event) => {
    const pageButton = event.target.closest('[data-unified-page]');
    if (pageButton && !pageButton.disabled) {
      state.page = Number(pageButton.getAttribute('data-unified-page')) || 1;
      loadOrders();
      return;
    }
    const orderLink = event.target.closest('[data-unified-order-key]');
    if (orderLink) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      openOrder(orderLink.getAttribute('data-unified-order-key'), true);
    }
  });

  results.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const row = event.target.closest('tr[data-unified-order-key]');
    if (!row || !results.contains(row)) return;
    event.preventDefault();
    openOrder(row.getAttribute('data-unified-order-key'), true);
  });

  if (!document.documentElement.dataset.accountPageTitle) {
    document.documentElement.dataset.accountPageTitle = document.title;
  }

  if (backButton) backButton.addEventListener('click', () => showList(true));
  window.addEventListener('popstate', () => {
    const orderKey = new URLSearchParams(window.location.search).get('order');
    if (orderKey) openOrder(orderKey, false);
    else showList(false);
  });

  const initialOrderKey = new URLSearchParams(window.location.search).get('order');
  loadOrders();
  if (initialOrderKey) openOrder(initialOrderKey, false);
})();
